import React from "react";
import * as Sentry from "@sentry/react";
import "./index.css";
import * as serviceWorker from "./serviceWorker";

import "bootstrap/dist/css/bootstrap.css";
import { BrowserRouter } from "react-router-dom";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import i18n from "./Themes/Tseppas/i18n";

// Initialize Sentry error monitoring
if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: process.env.NODE_ENV,
        integrations: [
            Sentry.browserTracingIntegration(),
        ],
        // Capture 20% of transactions for performance monitoring
        tracesSampleRate: 0.2,
        // Only send errors from your domain, not browser extensions
        allowUrls: [/https?:\/\/(.*\.)?nasnav\.com/, /https?:\/\/(.*\.)?tseppas\.com/, /localhost/],
        // Don't send errors in development unless DSN is set
        enabled: Boolean(process.env.REACT_APP_SENTRY_DSN),
    });
}

function importBuildTarget() {
    /* global process */
    if (process.env.REACT_APP_BUILD_YESHTERY === "true") {
        return import("./yeshtery/App");
    } else {
        return import("./App");
    }
}

const container = document.getElementById("root");
const root = createRoot(container);

importBuildTarget()
    .then(({ default: ImportedApp }) => {
        root.render(
            <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
                <BrowserRouter>
                    <I18nextProvider i18n={i18n}>
                        <ImportedApp />
                    </I18nextProvider>
                </BrowserRouter>
            </Sentry.ErrorBoundary>
        );
    })
    .catch((e) => console.error("BUILD TARGET ERROR:", e));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();

// Builder mode — activates when ?builder=true is in the URL
(function() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('builder') !== 'true') return;
    if (window === window.top) return; // Only run inside an iframe

    // Determine allowed parent origin from referrer or accept localhost for dev
    var ALLOWED_ORIGINS = [
        window.location.origin, // same-origin
    ];
    // Accept any localhost origin for local development
    function isAllowedOrigin(origin) {
        if (!origin) return false;
        if (origin.indexOf('://localhost') !== -1) return true;
        if (origin.indexOf('.meetusvr.com') !== -1) return true;
        if (origin.indexOf('.meetusar.com') !== -1) return true;
        return ALLOWED_ORIGINS.indexOf(origin) !== -1;
    }

    function postToParent(data) {
        window.parent.postMessage(data, '*');
    }

    var overlays = [];
    var inspectActive = false;
    var observer = null;
    var scrollHandler = null;
    var resizeHandler = null;
    var readyTimers = [];

    function getBackgroundImageUrl(el) {
        var bg = window.getComputedStyle(el).backgroundImage;
        if (!bg || bg === 'none') return null;
        var match = bg.match(/url\(["']?(.*?)["']?\)/);
        return match ? match[1] : null;
    }

    function createOverlay(targetEl, imageUrl, label) {
        var rect = targetEl.getBoundingClientRect();
        if (rect.width < 10 || rect.height < 10) return;
        var scrollX = window.scrollX || window.pageXOffset;
        var scrollY = window.scrollY || window.pageYOffset;

        var overlay = document.createElement('div');
        overlay.className = 'builder-inspect-overlay';
        overlay.style.cssText =
            'position:absolute;background:rgba(107,63,160,0.15);border:2px solid #6B3FA0;' +
            'cursor:crosshair;z-index:99999;pointer-events:auto;transition:background .15s;' +
            'box-sizing:border-box;left:' + (rect.left + scrollX) + 'px;top:' + (rect.top + scrollY) +
            'px;width:' + rect.width + 'px;height:' + rect.height + 'px;';

        var tip = document.createElement('span');
        tip.textContent = label;
        tip.style.cssText =
            'position:absolute;top:2px;left:2px;background:#6B3FA0;color:#fff;font-size:10px;' +
            'padding:2px 6px;border-radius:3px;pointer-events:none;font-family:sans-serif;white-space:nowrap;';
        overlay.appendChild(tip);

        overlay.onmouseenter = function() { overlay.style.background = 'rgba(107,63,160,0.35)'; };
        overlay.onmouseleave = function() { overlay.style.background = 'rgba(107,63,160,0.15)'; };
        overlay.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            postToParent({ type: 'inspect:clicked', url: imageUrl });
        }, true);

        overlay._targetEl = targetEl;
        overlay._imageUrl = imageUrl;
        document.body.appendChild(overlay);
        overlays.push(overlay);
    }

    function removeOverlays() {
        for (var i = 0; i < overlays.length; i++) {
            if (overlays[i].parentNode) overlays[i].parentNode.removeChild(overlays[i]);
        }
        overlays = [];
    }

    function scanAndOverlay() {
        removeOverlays();
        var imgs = document.querySelectorAll('img');
        for (var i = 0; i < imgs.length; i++) {
            var src = imgs[i].src || imgs[i].getAttribute('src');
            if (src && imgs[i].offsetWidth > 10) createOverlay(imgs[i], src, 'img');
        }
        var containers = document.querySelectorAll('div, section, header, footer');
        for (var j = 0; j < containers.length; j++) {
            var bgUrl = getBackgroundImageUrl(containers[j]);
            if (bgUrl) createOverlay(containers[j], bgUrl, 'bg');
        }
    }

    function updateOverlayPositions() {
        for (var i = 0; i < overlays.length; i++) {
            var t = overlays[i]._targetEl;
            if (!t || !t.parentNode) continue;
            var r = t.getBoundingClientRect();
            var sx = window.scrollX || window.pageXOffset;
            var sy = window.scrollY || window.pageYOffset;
            overlays[i].style.left = (r.left + sx) + 'px';
            overlays[i].style.top = (r.top + sy) + 'px';
            overlays[i].style.width = r.width + 'px';
            overlays[i].style.height = r.height + 'px';
        }
    }

    // Throttle helper
    function throttle(fn, ms) {
        var last = 0;
        return function() {
            var now = Date.now();
            if (now - last >= ms) { last = now; fn(); }
        };
    }

    function startInspect() {
        if (inspectActive) return;
        inspectActive = true;
        scanAndOverlay();

        // Throttled scroll repositioning
        scrollHandler = throttle(updateOverlayPositions, 100);
        window.addEventListener('scroll', scrollHandler, true);

        // Re-scan on resize
        resizeHandler = function() { if (inspectActive) scanAndOverlay(); };
        window.addEventListener('resize', resizeHandler);

        // Re-scan on React re-renders
        observer = new MutationObserver(function() {
            if (!inspectActive) return;
            clearTimeout(observer._t);
            observer._t = setTimeout(scanAndOverlay, 500);
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    function stopInspect() {
        inspectActive = false;
        removeOverlays();

        // Clean up all listeners
        if (scrollHandler) { window.removeEventListener('scroll', scrollHandler, true); scrollHandler = null; }
        if (resizeHandler) { window.removeEventListener('resize', resizeHandler); resizeHandler = null; }
        if (observer) { observer.disconnect(); observer = null; }
    }

    function highlightElement(url) {
        var filename = url.split('/').pop().split('?')[0];
        for (var i = 0; i < overlays.length; i++) {
            var ovFile = (overlays[i]._imageUrl || '').split('/').pop().split('?')[0];
            if (filename && ovFile === filename) {
                overlays[i]._targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                overlays[i].style.border = '4px solid #ff4444';
                overlays[i].style.background = 'rgba(255,68,68,0.3)';
                (function(ov) {
                    setTimeout(function() {
                        ov.style.border = '2px solid #6B3FA0';
                        ov.style.background = 'rgba(107,63,160,0.15)';
                    }, 3000);
                })(overlays[i]);
                return;
            }
        }
    }

    // Listen for parent messages — validate origin
    window.addEventListener('message', function(event) {
        if (!isAllowedOrigin(event.origin)) return;
        if (!event.data || !event.data.type) return;
        switch (event.data.type) {
            case 'inspect:start': startInspect(); break;
            case 'inspect:stop': stopInspect(); break;
            case 'inspect:highlight': if (event.data.url) highlightElement(event.data.url); break;
            case 'schema:request':
                // Dashboard requests the theme schema — dynamically import and respond
                import('./themeSchemas/index').then(function(mod) {
                    var themeId = event.data.themeId;
                    var schema = themeId ? mod.getThemeSchema(Number(themeId)) : null;
                    var allIds = mod.getAllThemeIds();
                    postToParent({
                        type: 'schema:response',
                        themeId: themeId,
                        schema: schema,
                        availableThemes: allIds,
                    });
                });
                break;
            case 'settings:update':
                // Dashboard sends updated settings — broadcast to React app
                if (event.data.settings) {
                    window.dispatchEvent(new CustomEvent('theme-settings-update', {
                        detail: event.data.settings,
                    }));
                }
                break;
            case 'sections:reorder':
            case 'sections:add':
            case 'sections:remove':
                // Dashboard sends section mutations — broadcast to React app
                if (event.data.payload) {
                    window.dispatchEvent(new CustomEvent('theme-sections-update', {
                        detail: {
                            action: event.data.type.split(':')[1],
                            ...event.data.payload,
                        },
                    }));
                }
                break;
            case 'categories:reorder':
                if (event.data.payload && event.data.payload.order) {
                    window.dispatchEvent(new CustomEvent('theme-categories-reorder', {
                        detail: { order: event.data.payload.order },
                    }));
                }
                break;
        }
    });

    // Notify parent — retry until acknowledged, then stop
    function notifyReady() {
        postToParent({ type: 'builder:ready' });
        // Send current org images so the sidebar can show them
        try {
            var orgData = window.__NASNAV_ORG_DATA__;
            if (orgData) {
                postToParent({
                    type: 'builder:orgData',
                    sliderImages: orgData.themes?.slider_images_urls || [],
                    logoUrl: orgData.themes?.logo_url || '',
                    orgName: orgData.name || '',
                });
            }
        } catch (e) {}
    }
    notifyReady();
    window.addEventListener('load', notifyReady);
    readyTimers.push(setTimeout(notifyReady, 2000));
    readyTimers.push(setTimeout(notifyReady, 5000));

    // ========================================
    // Section Overlay System — visual builder
    // ========================================
    var sectionOverlays = [];
    var activeSectionId = null;

    // Inject builder overlay styles
    var styleEl = document.createElement('style');
    styleEl.textContent = [
        '[data-builder-section] { position: relative; }',
        '.builder-section-outline { position: absolute; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; z-index: 9998; border: 2px solid transparent; transition: border-color 0.2s, background 0.2s; }',
        '[data-builder-section]:hover > .builder-section-outline { border-color: #6B3FA0; background: rgba(107,63,160,0.04); }',
        '.builder-section-toolbar { position: absolute; top: 8px; right: 8px; display: flex; gap: 6px; opacity: 0; transition: opacity 0.2s; pointer-events: auto; z-index: 9999; }',
        '[data-builder-section]:hover > .builder-section-outline > .builder-section-toolbar { opacity: 1; }',
        '.builder-section-btn { width: 36px; height: 36px; border-radius: 50%; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); transition: transform 0.15s; }',
        '.builder-section-btn:hover { transform: scale(1.1); }',
        '.builder-section-btn-edit { background: #6B3FA0; color: #fff; }',
        '.builder-section-btn-move { background: #fff; color: #333; }',
        '.builder-section-btn-delete { background: #fff; color: #e74c3c; }',
        '.builder-section-label { position: absolute; top: 8px; left: 8px; background: #6B3FA0; color: #fff; font-size: 11px; padding: 3px 10px; border-radius: 3px; font-family: -apple-system, sans-serif; pointer-events: none; opacity: 0; transition: opacity 0.2s; z-index: 9999; }',
        '[data-builder-section]:hover > .builder-section-outline > .builder-section-label { opacity: 1; }',
        '.builder-section-active > .builder-section-outline { border-color: #6B3FA0 !important; background: rgba(107,63,160,0.06) !important; }',
        '.builder-section-active > .builder-section-outline > .builder-section-toolbar { opacity: 1 !important; }',
        '.builder-section-active > .builder-section-outline > .builder-section-label { opacity: 1 !important; }',
        '.builder-add-btn { display: flex; align-items: center; justify-content: center; margin: 0 auto; width: 36px; height: 36px; border-radius: 50%; background: #6B3FA0; color: #fff; border: none; font-size: 22px; cursor: pointer; opacity: 0; transition: opacity 0.2s; z-index: 9998; position: relative; }',
        '.builder-add-zone { height: 0; display: flex; align-items: center; justify-content: center; position: relative; z-index: 9998; }',
        '.builder-add-zone:hover { height: 48px; }',
        '.builder-add-zone:hover .builder-add-btn { opacity: 1; }',
    ].join('\n');
    document.head.appendChild(styleEl);

    function injectSectionOverlays() {
        // Remove old overlays
        sectionOverlays.forEach(function(el) { if (el.parentNode) el.parentNode.removeChild(el); });
        sectionOverlays = [];

        var sections = document.querySelectorAll('[data-builder-section]');
        sections.forEach(function(section) {
            // Skip if already has overlay
            if (section.querySelector('.builder-section-outline')) return;

            var sectionId = section.getAttribute('data-builder-section');
            var pageId = section.getAttribute('data-builder-page');
            var label = section.getAttribute('data-builder-label') || sectionId;

            // Create outline container
            var outline = document.createElement('div');
            outline.className = 'builder-section-outline';

            // Label
            var labelEl = document.createElement('div');
            labelEl.className = 'builder-section-label';
            labelEl.textContent = label;
            outline.appendChild(labelEl);

            // Toolbar
            var toolbar = document.createElement('div');
            toolbar.className = 'builder-section-toolbar';

            // Edit button
            var editBtn = document.createElement('button');
            editBtn.className = 'builder-section-btn builder-section-btn-edit';
            editBtn.innerHTML = '&#9998;'; // pencil
            editBtn.title = 'Edit ' + label;
            editBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                // Deactivate previous
                var prev = document.querySelector('.builder-section-active');
                if (prev) prev.classList.remove('builder-section-active');
                // Activate this
                section.classList.add('builder-section-active');
                activeSectionId = sectionId;
                // Tell dashboard to open this section
                postToParent({
                    type: 'section:edit',
                    sectionId: sectionId,
                    pageId: pageId,
                    label: label,
                });
            });
            toolbar.appendChild(editBtn);

            // Move button
            var moveBtn = document.createElement('button');
            moveBtn.className = 'builder-section-btn builder-section-btn-move';
            moveBtn.innerHTML = '&#8597;'; // up-down arrows
            moveBtn.title = 'Move ' + label;
            moveBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                postToParent({
                    type: 'section:move',
                    sectionId: sectionId,
                    pageId: pageId,
                });
            });
            toolbar.appendChild(moveBtn);

            outline.appendChild(toolbar);
            section.appendChild(outline);
            sectionOverlays.push(outline);
        });
    }

    // Inject overlays when DOM is ready and on React re-renders
    function startSectionOverlays() {
        injectSectionOverlays();
        var sectionObserver = new MutationObserver(function() {
            clearTimeout(sectionObserver._t);
            sectionObserver._t = setTimeout(injectSectionOverlays, 500);
        });
        sectionObserver.observe(document.body, { childList: true, subtree: true });
    }

    // Start after page load
    if (document.readyState === 'complete') {
        setTimeout(startSectionOverlays, 1000);
    } else {
        window.addEventListener('load', function() {
            setTimeout(startSectionOverlays, 1000);
        });
    }
})();
